import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell, MessageCircle, MessageSquare, UserPlus, Briefcase, ShieldAlert, FileCheck, FileX, Ban, PauseCircle, Smartphone, CheckCircle2, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useNotifications,
  markNotificationsSeen,
  type NotificationKind,
} from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';

const KIND_ICON: Record<NotificationKind, typeof Bell> = {
  message: MessageCircle,
  lead: UserPlus,
  job: Briefcase,
  dispute: ShieldAlert,
  estimate_accepted: FileCheck,
  estimate_declined: FileX,
  portal_message: MessageSquare,
  tech_declined: Ban,
  tech_on_hold: PauseCircle,
  tech_quote_sent: Smartphone,
  job_closed_by_tech: CheckCircle2,
  xero_sync_issue: BookOpen,
};

const KIND_STYLE: Record<NotificationKind, string> = {
  message: 'bg-info/10 text-info',
  lead: 'bg-primary/10 text-primary',
  job: 'bg-warning/10 text-warning',
  dispute: 'bg-destructive/10 text-destructive',
  estimate_accepted: 'bg-success/10 text-success',
  estimate_declined: 'bg-destructive/10 text-destructive',
  portal_message: 'bg-info/10 text-info',
  tech_declined: 'bg-destructive/10 text-destructive',
  tech_on_hold: 'bg-warning/10 text-warning',
  tech_quote_sent: 'bg-primary/10 text-primary',
  job_closed_by_tech: 'bg-success/10 text-success',
  xero_sync_issue: 'bg-warning/10 text-warning',
};

export function NotificationsPanel() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useNotifications();

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Opening the panel is the "read" action — clear the badge on close so the
    // list the user is looking at keeps its unread styling while it's open.
    if (!next && unreadCount > 0) {
      markNotificationsSeen();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  };

  const handleSelect = (href: string) => {
    markNotificationsSeen();
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    setOpen(false);
    navigate(href);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-9 w-9"
          aria-label={
            unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
          }
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[380px] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-[13px] font-semibold">Notifications</p>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">{unreadCount} new</span>
          )}
        </div>

        {isLoading ? (
          <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">
            You're all caught up
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <ul className="divide-y">
              {items.map((item) => {
                const Icon = KIND_ICON[item.kind];
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(item.href)}
                      className="flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <span
                        className={cn(
                          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md',
                          KIND_STYLE[item.kind]
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium">
                          {item.title}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {item.description}
                        </span>
                        <span className="mt-1 block text-[11px] text-muted-foreground">
                          {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
